import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import PdfButton from "@/components/PdfButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Bug } from "lucide-react";

// Local (not UTC) calendar date so AU morning shifts don't default to yesterday.
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function PestControl() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const canEdit = user?.role !== "auditor";

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    visit_date: todayLocal(),
    contractor: "",
    activity: "routine",
    findings: "",
    action_taken: "",
  });

  const load = async () => {
    if (!activeSite) return;
    const { data } = await api.get("/pest-logs", withSite(activeSite.id));
    setItems(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeSite]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/pest-logs", form, withSite(activeSite.id));
      toast.success("Pest log recorded");
      setForm({ ...form, findings: "", action_taken: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="animate-fade-in" data-testid="pest-page">
      <PageHeader
        title="Pest Control"
        subtitle="Record contractor visits, bait-station checks, pest sightings and actions taken."
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="pest" />
            <PdfButton kind="pest" />
          </div>
        }
      />

      {canEdit && (
        <form onSubmit={submit} className="surface p-6 md:p-8 mb-8 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-3 flex items-center gap-2">
            <Bug className="w-5 h-5 text-eucalyptus-700" />
            <h2 className="font-display font-bold text-lg">New pest log</h2>
          </div>
          <div className="space-y-2">
            <Label>Visit date</Label>
            <Input data-testid="p-date" type="date" value={form.visit_date}
              onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="h-12" required />
          </div>
          <div className="space-y-2">
            <Label>Contractor / Inspector</Label>
            <Input data-testid="p-contractor" value={form.contractor}
              onChange={(e) => setForm({ ...form, contractor: e.target.value })} className="h-12" />
          </div>
          <div className="space-y-2">
            <Label>Activity</Label>
            <Select value={form.activity} onValueChange={(v) => setForm({ ...form, activity: v })}>
              <SelectTrigger data-testid="p-activity" className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine service</SelectItem>
                <SelectItem value="treatment">Treatment</SelectItem>
                <SelectItem value="infestation">Infestation</SelectItem>
                <SelectItem value="sighting">Sighting</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-2">
            <Label>Findings</Label>
            <Textarea data-testid="p-findings" rows={2} value={form.findings}
              onChange={(e) => setForm({ ...form, findings: e.target.value })} />
          </div>
          <div className="md:col-span-3 space-y-2">
            <Label>Action taken</Label>
            <Textarea data-testid="p-action" rows={2} value={form.action_taken}
              onChange={(e) => setForm({ ...form, action_taken: e.target.value })} />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button
              type="submit"
              data-testid="p-submit"
              className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
            >
              Save log
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="surface p-10 text-center text-charcoal-soft">No pest logs yet.</div>
        )}
        {items.map((l) => (
          <div key={l.id} className="surface p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="label-eyebrow">{l.activity}</span>
                <span className="font-mono text-xs text-charcoal-soft">{l.visit_date}</span>
              </div>
              <span className="text-xs font-mono text-charcoal-soft">
                {new Date(l.created_at).toLocaleString("en-AU")} · {l.user_name}
              </span>
            </div>
            <div className="mt-2 font-medium">{l.contractor || "—"}</div>
            {l.findings && <div className="mt-2 text-sm"><b>Findings:</b> {l.findings}</div>}
            {l.action_taken && <div className="text-sm"><b>Action:</b> {l.action_taken}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
